/// <reference types="node" />

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { profileService } from "@/services/profileService";
import { adminService } from "@/services/adminService";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import type { AdminStatusValue } from "@/context/AdminStatusContext";
import { getDashboardProfile, shouldDeferToDashboardBatch } from "@/utils/dashboardQueryCache";
import { isTransientNetworkError } from "@/utils/transientNetworkError";

function isAdminRole(role: string | undefined | null): boolean {
  return role === "admin" || role === "super_admin";
}

function isIgnorableProfileError(error: unknown): boolean {
  const profileError = error as Error & { code?: string };
  return (
    profileError.code === "NETWORK_CHANGED" ||
    Boolean(profileError.message?.includes("Network connection changed")) ||
    isTransientNetworkError(error)
  );
}

/**
 * Resolve Profile For Admin Check.
 */
function resolveProfileForAdminCheck(
  userId: string,
  seededProfile: Awaited<ReturnType<typeof profileService.fetchProfile>> | undefined,
  queryClient: ReturnType<typeof useQueryClient>
) {
  if (seededProfile !== undefined) {
    return seededProfile;
  }

  return queryClient.fetchQuery({
    queryKey: ["profile", userId],
    queryFn: () => profileService.fetchProfile(userId),
    staleTime: 5 * 60 * 1000,
  });
}

function shouldUseCachedAdminStatus(
  cached: { isAdmin: boolean; timestamp: number } | undefined,
  now: number
): cached is { isAdmin: boolean; timestamp: number } {
  return Boolean(cached && now - cached.timestamp < 300000);
}

function shouldDeferAdminCheck(
  pathname: string,
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  seededProfile: ReturnType<typeof getDashboardProfile>
): boolean {
  const onDashboard = pathname === "/dashboard";
  const deferBatch = onDashboard && shouldDeferToDashboardBatch(queryClient, userId, pathname);
  return deferBatch && seededProfile === undefined;
}

/**
 * Report Admin Status Error.
 */
function reportAdminStatusError(error: unknown): void {
  if (isIgnorableProfileError(error)) {
    return;
  }

  const errorMessage = error instanceof Error ? error.message : "Unknown error";
  console.error("Error checking admin status:", error);
  toast({
    title: "Error",
    description: `Failed to check admin status: ${errorMessage}`,
    variant: "destructive",
  });
}

/**
 * Resolve Admin From Profile.
 */
async function resolveAdminFromProfile(
  userId: string,
  seededProfile: ReturnType<typeof getDashboardProfile>,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<boolean | null> {
  try {
    const profileData = await resolveProfileForAdminCheck(userId, seededProfile, queryClient);
    return profileData && isAdminRole(profileData.role) ? true : null;
  } catch (profileError) {
    if (!isIgnorableProfileError(profileError)) {
      console.error("Error checking profile:", profileError);
    }
    return null;
  }
}

/**
 * Internal admin-status logic — mount once via AdminStatusProvider so Header/Sidebar
 * do not each trigger separate profile + admin API checks.
 */
export function useAdminStatusState(): AdminStatusValue {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const lastCheckRef = useRef<number>(0);

  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cachedAdminStatus = useRef<{ [userId: string]: { isAdmin: boolean; timestamp: number } }>(
    {}
  );

  const checkAdminStatus = useCallback(async () => {
    if (!user || !session?.access_token) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    const cacheKey = user.id;
    const cached = cachedAdminStatus.current[cacheKey];
    const now = Date.now();

    if (shouldUseCachedAdminStatus(cached, now)) {
      setIsAdmin(cached.isAdmin);
      setIsLoading(false);
      return;
    }

    if (now - lastCheckRef.current < 10000) {
      return;
    }

    const seededProfile = getDashboardProfile(queryClient, user.id);
    if (shouldDeferAdminCheck(pathname, queryClient, user.id, seededProfile)) {
      return;
    }

    lastCheckRef.current = now;
    setIsLoading(true);

    try {
      const profileAdminStatus = await resolveAdminFromProfile(user.id, seededProfile, queryClient);
      if (profileAdminStatus === true) {
        setIsAdmin(true);
        cachedAdminStatus.current[cacheKey] = { isAdmin: true, timestamp: now };
        return;
      }

      const adminStatus = await adminService.checkAdmin();
      setIsAdmin(adminStatus);
      cachedAdminStatus.current[cacheKey] = { isAdmin: adminStatus, timestamp: now };
    } catch (error: unknown) {
      reportAdminStatusError(error);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, [user, session?.access_token, queryClient, pathname]);

  useEffect(() => {
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    checkTimeoutRef.current = setTimeout(() => {
      checkAdminStatus();
    }, 500);

    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [checkAdminStatus, pathname]);

  // Re-check admin status after dashboard batch seeds profile cache
  useEffect(() => {
    if (pathname !== "/dashboard" || !user?.id) {
      return undefined;
    }

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        event.query.queryKey[0] === "dashboard" &&
        event.query.queryKey[1] === user.id &&
        event.query.state.status === "success"
      ) {
        checkAdminStatus();
      }
    });

    return unsubscribe;
  }, [checkAdminStatus, pathname, queryClient, user?.id]);

  const refreshAdminStatus = () => checkAdminStatus();

  return { isAdmin, isLoading, refreshAdminStatus };
}
